import { useQuery } from '@tanstack/react-query';
import { useCallback, useMemo, useState } from 'react';
import { useAppContext } from 'src/contexts/AppContext';
import {
  useCatalogDataContextEventsExpansionSelector,
  useCatalogDataContextRefreshEventForItemsSelector,
} from 'src/contexts/CatalogDataContext';
import { useClientLogContext } from 'src/contexts/ClientLogContext';
import { useReportedMissingSections } from 'src/contexts/ReportedMissingSectionsContext';
import { useGetSellerEventWithVenueByVenueConfigId } from 'src/hooks/api/useGetSellerEventWithVenueByVenueConfigId';
import { useUserHasFeature } from 'src/hooks/useUserHasFeature';
import { isSuccess } from 'src/utils/errorUtils';
import { tryInvokeApi } from 'src/utils/tryExecuteUtils';
import {
  ActionOutboxEntityType,
  BulkEditListingClient,
  CatalogReportClient,
  Feature,
  FileParameter,
  Listing,
  ListingClient,
  Marketplace,
  SectionInfo,
  VenueMapReportType,
} from 'src/WebApiController';

import {
  buildBulkSectionOverrideInput,
  buildSameSectionBulkQuery,
  buildSectionMappingInput,
} from './SectionMappingControl.utils';

/**
 * Applies a StubHub section mapping to a single listing via the same
 * broadcast-section endpoint used by the listing details page, then refreshes
 * the listing in the catalog so the unmapped indicator clears.
 */
export const useApplySectionMapping = (listing: Listing) => {
  const { activeAccountWebClientConfig } = useAppContext();
  const { showErrorDialog } = useClientLogContext();
  const refreshEventForItems =
    useCatalogDataContextRefreshEventForItemsSelector();
  const [isApplying, setIsApplying] = useState(false);

  const applyMapping = useCallback(
    async (section: SectionInfo, persistOverride = false): Promise<boolean> => {
      setIsApplying(true);
      const input = buildSectionMappingInput(listing, section, persistOverride);
      let succeeded = false;
      await tryInvokeApi(
        async () => {
          const result = await new ListingClient(
            activeAccountWebClientConfig
          ).updateListingBroadcastSectionByListingId(listing.id, input);

          if (isSuccess(result)) {
            succeeded = true;
            refreshEventForItems([listing.id], ActionOutboxEntityType.Listing);
          } else {
            showErrorDialog(
              'ListingClient.updateListingBroadcastSection',
              { message: result.message, status: result.status },
              { trackErrorData: { input } }
            );
          }
        },
        (error) => {
          showErrorDialog(
            'ListingClient.updateListingBroadcastSection',
            error,
            { trackErrorData: { input } }
          );
        },
        () => {
          setIsApplying(false);
        }
      );
      return succeeded;
    },
    [
      activeAccountWebClientConfig,
      listing,
      refreshEventForItems,
      showErrorDialog,
    ]
  );

  return { applyMapping, isApplying };
};

/**
 * FR1: reports the listing's section as missing from the StubHub section list
 * (POS-9306). Creates an "Incorrect Venue Map" catalog task (EDM TaskType 47)
 * via the WorkItem service, and on success marks the listing as "requested" so
 * its mapping indicator changes and duplicate reports are avoided (FR2/FR7).
 */
export const useReportMissingSection = (
  listing: Listing,
  venueConfigId: number | null | undefined
) => {
  const { activeAccountWebClientConfig } = useAppContext();
  const { showErrorDialog } = useClientLogContext();
  const { markReported } = useReportedMissingSections();
  const [isReporting, setIsReporting] = useState(false);

  const reportMissingSection = useCallback(
    async (
      description: string,
      attachments: File[],
      proofUrl: string
    ): Promise<boolean> => {
      if (listing.viagEvId == null) {
        return false;
      }
      setIsReporting(true);
      let succeeded = false;
      const fileParams: FileParameter[] = attachments.map((f) => ({
        data: f,
        fileName: f.name,
      }));
      // The WorkItem service's ListingId is the StubHub marketplace listing id
      // (FK to viagogo.dbo.Listing), not the POS listing id.
      const stubhubMkpListingId = listing.mkpListings?.find(
        (m) => m.mkp === Marketplace.StubHub
      )?.mkpListingId;
      const stubhubListingId =
        stubhubMkpListingId && Number.isFinite(Number(stubhubMkpListingId))
          ? Number(stubhubMkpListingId)
          : null;
      await tryInvokeApi(
        async () => {
          const result = await new CatalogReportClient(
            activeAccountWebClientConfig
          ).reportVenueMapIssue(
            VenueMapReportType.MissingSection,
            listing.viagEvId ?? undefined,
            venueConfigId ?? null,
            null,
            listing.seating.section ?? null,
            stubhubListingId,
            description || null,
            proofUrl || null,
            fileParams.length > 0 ? fileParams : null
          );
          if (result?.workItemId != null) {
            succeeded = true;
            markReported(venueConfigId, listing.seating.section);
          }
        },
        (error) => {
          showErrorDialog('CatalogReportClient.reportVenueMapIssue', error, {
            trackErrorData: { listingId: stubhubListingId },
          });
        },
        () => {
          setIsReporting(false);
        }
      );
      return succeeded;
    },
    [
      activeAccountWebClientConfig,
      listing,
      venueConfigId,
      markReported,
      showErrorDialog,
    ]
  );

  return { reportMissingSection, isReporting };
};

/**
 * FR6: fetches the prefill proof/scrape URL for the event's mapping so the
 * missing-section report can pre-populate it. The seller can still override it.
 */
export const useEventProofUrl = (
  viagEvId: number | null | undefined,
  enabled: boolean
) => {
  const { activeAccountWebClientConfig } = useAppContext();
  const hasFeature = useUserHasFeature(Feature.CatalogSupport);

  const query = useQuery({
    queryKey: [
      'CatalogReportClient.getEventProofUrls',
      activeAccountWebClientConfig.activeAccountId,
      viagEvId,
    ],
    queryFn: async () => {
      if (viagEvId == null) {
        return null;
      }
      const results = await new CatalogReportClient(
        activeAccountWebClientConfig
      ).getEventProofUrls([viagEvId]);
      return results?.[0]?.url ?? null;
    },
    enabled: hasFeature && enabled && viagEvId != null,
    refetchOnWindowFocus: false,
  });

  return { proofUrl: query.data ?? null };
};

/**
 * FR4 bulk apply: offers applying the same section override to all listings
 * sharing the source listing's raw section name across events on the same venue
 * configuration. Previews the affected count and applies via the existing bulk
 * marketplace-seating-override endpoints.
 *
 * @param enabled gate preview fetching (e.g. only when the popover is open).
 */
export const useSectionMappingBulk = (
  listing: Listing,
  venueConfigId: number | null | undefined,
  enabled: boolean
) => {
  const { activeAccountWebClientConfig } = useAppContext();
  const { showErrorDialog } = useClientLogContext();
  const { refreshExpandedListItems } =
    useCatalogDataContextEventsExpansionSelector();
  const [isApplying, setIsApplying] = useState(false);

  // Proactively fetch every seller event on this venue config from the backend
  // rather than relying on the loaded catalog — the catalog only holds the
  // current event on the event page, which would otherwise miss sibling events.
  const { sellerEventsWithSameVenueConfigId } =
    useGetSellerEventWithVenueByVenueConfigId(enabled ? venueConfigId : null);

  const rawSection = listing.seating.section;
  const eventVirtIds = useMemo(
    () =>
      // Section mapping only applies to mapped events, whose viagVirtId is the
      // viagogo event id as a string.
      sellerEventsWithSameVenueConfigId?.map((e) =>
        e.viagogoEventId.toString()
      ) ?? [],
    [sellerEventsWithSameVenueConfigId]
  );
  const scopedQuery = useMemo(
    () => buildSameSectionBulkQuery(eventVirtIds, rawSection),
    [eventVirtIds, rawSection]
  );

  const canPreview =
    enabled && venueConfigId != null && !!rawSection && eventVirtIds.length > 0;

  const previewQuery = useQuery({
    queryKey: [
      'BulkEditListingClient.bulkUpdateMarketplaceSeatingOverridesPreview',
      activeAccountWebClientConfig.activeAccountId,
      venueConfigId,
      rawSection,
      eventVirtIds,
    ],
    queryFn: async () => {
      if (!canPreview) {
        return null;
      }
      return new BulkEditListingClient(
        activeAccountWebClientConfig
      ).bulkUpdateMarketplaceSeatingOverridesPreview(scopedQuery);
    },
    enabled: canPreview,
    refetchOnWindowFocus: false,
  });

  const bulkApply = useCallback(
    async (
      section: SectionInfo,
      updateKey: string,
      supportBackgroundProcess: boolean,
      overrideRowIfNotPassed: boolean
    ): Promise<boolean> => {
      const preview = previewQuery.data;
      if (!preview) {
        return false;
      }
      setIsApplying(true);
      const listingUpdate = buildBulkSectionOverrideInput(section);
      let succeeded = false;
      await tryInvokeApi(
        async () => {
          const ok = await new BulkEditListingClient(
            activeAccountWebClientConfig
          ).bulkUpdateMarketplaceSeatingOverrides(
            { item1: preview.preview, item2: listingUpdate },
            updateKey,
            supportBackgroundProcess,
            overrideRowIfNotPassed
          );
          if (ok) {
            succeeded = true;
            // In background mode the job continues async; the refresh happens
            // when the SignalR hub reports completion (onBulkEditDone).
            if (!supportBackgroundProcess) {
              await refreshExpandedListItems();
            }
          }
        },
        (error) => {
          showErrorDialog(
            'BulkEditListingClient.bulkUpdateMarketplaceSeatingOverrides',
            error,
            { trackErrorData: { listingUpdate } }
          );
        },
        () => {
          setIsApplying(false);
        }
      );
      return succeeded;
    },
    [
      activeAccountWebClientConfig,
      previewQuery.data,
      refreshExpandedListItems,
      showErrorDialog,
    ]
  );

  return {
    preview: previewQuery.data ?? null,
    affectedCount: previewQuery.data?.allItemsCount,
    isPreviewLoading: previewQuery.isFetching,
    isApplying,
    bulkApply,
    refresh: refreshExpandedListItems,
  };
};
